import { ReactElement } from 'react'
import { Box, Card, Chip, LinearProgress, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { CalendarIcon } from 'src/v3/core/icons'
import { AssignmentDetail } from '../types'
import {
  getCompletedCourseCount,
  getTotalCourseCount,
  getProgressPercentage,
  getTimeUntilDue,
  isOverdue,
} from '../../utils/helpers'

type AssignmentDetailCardProps = {
  assignment: AssignmentDetail
  linkedServiceName?: string
}

export default function AssignmentDetailCard({
  assignment,
  linkedServiceName,
}: AssignmentDetailCardProps): ReactElement {
  let completedCount = getCompletedCourseCount(assignment)
  let totalCount = getTotalCourseCount(assignment)
  let progressPercentage = getProgressPercentage(assignment)
  let timeContext = getTimeUntilDue(assignment.dueDate)
  let overdueStatus = isOverdue(
    assignment.dueDate,
    assignment.status === 'Complete',
  )

  return (
    <Card
      sx={{ p: 4, bgcolor: 'background.paper' }}
      role="region"
      aria-label="Assignment details"
    >
      <Box className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
        {/* Created Date */}
        {assignment.dateCreated && (
          <Box role="group" aria-label="Assignment creation date">
            <Typography
              color="text.secondary"
              variant="caption"
              className="block mb-1"
              id="created-date-label"
            >
              Created on
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500 }}
              aria-labelledby="created-date-label"
            >
              {dayjs(assignment.dateCreated).format('MMM D, YYYY')}
            </Typography>
          </Box>
        )}

        {/* Due Date */}
        <Box role="group" aria-label="Assignment due date">
          <Typography
            color="text.secondary"
            variant="caption"
            className="block mb-1"
            id="due-date-label"
          >
            Due on
          </Typography>
          <Box className="flex items-center gap-2">
            <CalendarIcon
              sx={{
                fontSize: 20,
                color: overdueStatus ? 'error.main' : 'text.secondary',
              }}
              aria-hidden="true"
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: overdueStatus ? 'error.main' : 'text.primary',
              }}
              aria-labelledby="due-date-label"
            >
              {assignment.dueDate
                ? dayjs(assignment.dueDate).format('MMM D, YYYY')
                : 'No due date'}
            </Typography>
          </Box>
          {timeContext && (
            <Typography
              variant="body2"
              sx={{
                color: overdueStatus ? 'error.main' : 'text.secondary',
                mt: 0.5,
                fontWeight: overdueStatus ? 600 : 400,
              }}
              role={overdueStatus ? 'alert' : undefined}
            >
              {timeContext}
            </Typography>
          )}
        </Box>

        {/* Course Count */}
        <Box role="group" aria-label="Course count">
          <Typography
            color="text.secondary"
            variant="caption"
            className="block mb-1"
            id="course-count-label"
          >
            Courses
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontWeight: 500 }}
            aria-labelledby="course-count-label"
          >
            {totalCount} course{totalCount === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>
      {/* Linked Service */}
      {linkedServiceName && (
        <Box className="mb-4">
          <Typography
            color="text.secondary"
            variant="caption"
            className="block mb-1"
          >
            Assigned by
          </Typography>
          <Chip
            color="primary"
            label={linkedServiceName}
            size="small"
            variant="outlined"
            aria-label={`Assigned by ${linkedServiceName}`}
          />
        </Box>
      )}
      {/* Progress Section */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paperDark',
          borderRadius: 2,
        }}
        role="region"
        aria-labelledby="progress-heading"
      >
        <Box className="flex items-center justify-between mb-2">
          <Typography
            id="progress-heading"
            variant="body1"
            sx={{ fontWeight: 600 }}
          >
            Progress
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color:
                assignment.status === 'Complete'
                  ? 'success.main'
                  : 'primary.main',
              fontWeight: 700,
            }}
            aria-label={`${completedCount} of ${totalCount} courses completed`}
          >
            {completedCount} / {totalCount}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            color: assignment.status === 'Complete' ? 'success' : 'primary',
            height: 12,
            borderRadius: 6,
            mb: 1,
          }}
          aria-valuenow={progressPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Assignment progress: ${progressPercentage} percent complete`}
        />
        <Typography
          color="text.secondary"
          variant="body2"
          className="text-center"
          aria-hidden="true"
        >
          {progressPercentage}% complete
        </Typography>
      </Box>
    </Card>
  )
}
